"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import type { ReactNode } from "react";
import { isPublicPath } from "@/lib/public-paths";

import { getMe, logout as logoutApi, type AuthUser } from "@/lib/api/auth";

interface AuthContextValue {
  user: AuthUser | null;
  isAdmin: boolean;
  /** A Miton person: the /miton hub (watchlist, portfolio roles, Talent chat). Decided by the API. */
  isMiton: boolean;
  /** May open Miton Talent: decided by the API (role + @miton.cz + configured). */
  hasTalentAccess: boolean;
  isLoading: boolean;
  logout: () => void;
  /** Re-read /auth/me — for surfaces that change the user record itself. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAdmin: false,
  isMiton: false,
  hasTalentAccess: false,
  isLoading: true,
  logout: () => {},
  refresh: async () => {}
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchUser() {
      try {
        const me = await getMe();
        if (!cancelled) {
          if (me) {
            setUser(me);
          } else {
            // No valid session — redirect to login, but skip on public paths
            // (shared list in src/lib/public-paths.ts, same as the middleware).
            const path = window.location.pathname;
            if (!isPublicPath(path)) {
              const next = path + window.location.search;
              window.location.href = `/login?next=${encodeURIComponent(next)}`;
              return; // don't clear loading state — page is navigating
            }
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = useCallback(async () => {
    setUser(await getMe());
  }, []);

  const logout = useCallback(async () => {
    await logoutApi();
    setUser(null);
    window.location.href = "/";
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAdmin: user?.role === "admin",
      isMiton: Boolean(user?.miton_access),
      hasTalentAccess: Boolean(user?.talent_access),
      isLoading,
      logout,
      refresh
    }),
    [user, isLoading, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
